import { FC, useCallback, useEffect } from 'react'
import { FeedbackType, GrammarFeedback } from '@chegg-wt/core/types/paraphraser'
import getFeatureEditor from '@chegg-wt/core/utils/paraphraser/getEditor'
import {
  activeFeedbackCardIdSelector,
  enabledWAFeaturesSelector,
  isPaperCheckInProgressSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core/state/paraphraser/selectors'
import { useDispatch, useSelector } from 'react-redux'
import {
  getGrammarPosition,
  scrollMarkContentInEditor,
} from '@chegg-wt/core/utils/paraphraser/helpers'
import {
  ProofReaderCardActions,
  SentenceOffset,
} from '@chegg-wt/core/state/paraphraser/types'
import {
  deleteFeedback,
  setActiveFeedbackCardId,
} from '@chegg-wt/core/state/paraphraser/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import useSprigTriggerExitIntent, {
  SprigExitIntentKey,
} from '@chegg-wt/core/hooks/paraphraser/useSprigTriggerExitIntent'
import history from '@chegg-wt/core/utils/paraphraser/history/history'
import { HistoryDeltaAction } from '@chegg-wt/core/utils/paraphraser/history/types'
import { debounceOnSavePaper } from '@chegg-wt/core/state/paraphraser/utils'
import { setNextCardActiveAfterDeleteOrUpdate } from '../utils'
import { GrammarCardsContainer, GrammarSectionWrapper } from './styled'
import GrammarCard from './GrammarCard/GrammarCard'

type GrammarSectionProps = {
  grammarFeedbacks: GrammarFeedback[]
}

export const handleGrammarDeleteClick = (
  feedbackId: string,
  deletingFeedbackIndex: number,
  grammarFeedbacks: GrammarFeedback[],
  sentenceOffsets: SentenceOffset[],
  dispatch,
  setActiveCard,
  initiateSurveyOnCardInteraction
) => {
  const editor = getFeatureEditor()

  const { impactedText, sentenceOriginalText, ...feedback } = grammarFeedbacks[
    deletingFeedbackIndex
  ]
  const { id, suggestedText } = feedback

  // Add discard card change to history and commit
  history.addFeedbackChange({
    feedback,
    action: HistoryDeltaAction.DISCARD,
  })
  history.commit()

  dispatch(
    userClick({
      origin: origins.proofReaderFeedbackCardClick,
      text: impactedText,
      value: suggestedText,
      id,
      citationMetadata: {
        proofReaderCardtype: FeedbackType.GRAMMAR,
        proofReaderCardAction: ProofReaderCardActions.REJECT,
      },
    })
  )

  const { offset, length } = getGrammarPosition(
    grammarFeedbacks[deletingFeedbackIndex],
    sentenceOffsets
  )
  const { from, to } = editor.state.selection

  editor.commands.removeCustomUnderline({
    id: feedbackId,
    offset,
    length,
  })

  editor.commands.setTextSelection({ from, to })

  dispatch(deleteFeedback(feedbackId))

  setNextCardActiveAfterDeleteOrUpdate(
    deletingFeedbackIndex,
    grammarFeedbacks,
    setActiveCard
  )
  initiateSurveyOnCardInteraction(SprigExitIntentKey.EbPostCardInteractionSurveyKey)
  debounceOnSavePaper(dispatch) // As there is no text change we need to handle saving delete case specially
}

const GrammarSection: FC<GrammarSectionProps> = ({ grammarFeedbacks }) => {
  const dispatch = useDispatch()
  const activeCardId = useSelector(activeFeedbackCardIdSelector)
  const editor = getFeatureEditor()
  const sentenceOffsets = useSelector(sentenceOffsetsSelector)
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const setActiveCard = useCallback(
    (cardId: string) => dispatch(setActiveFeedbackCardId(cardId)),
    [dispatch]
  )
  const enabledWAFeaturesSelectorFlag = useSelector(enabledWAFeaturesSelector)
  const isUndoRedoEnabled = enabledWAFeaturesSelectorFlag.redoUndo

  const { initiateSurveyOnCardInteraction } = useSprigTriggerExitIntent()

  const grammarFeedbacksCount = grammarFeedbacks.length

  const removePreviousHighlight = useCallback(() => {
    const feedback = grammarFeedbacks.find((feedback) => feedback.id === activeCardId)

    if (!feedback) return
    const { offset, length } = getGrammarPosition(feedback, sentenceOffsets)
    const { from, to } = editor.state.selection

    editor.commands.removeCustomHighlight({
      id: feedback.id,
      offset,
      length,
    })
    editor.commands.setTextSelection({ from, to })
  }, [activeCardId, editor, grammarFeedbacks, sentenceOffsets])

  const handleCardClick = (feedbackId: string | null) => {
    if (activeCardId === feedbackId) {
      // Do nothing if current card and previous card are same
      return
    }
    removePreviousHighlight()
    setActiveCard(feedbackId)
    scrollMarkContentInEditor(feedbackId)
  }

  // Highlight the active card text on Editor
  useEffect(() => {
    // intialize the active card with index zero on first load, and update active card to the first index
    if (
      grammarFeedbacksCount > 0 &&
      (!activeCardId ||
        (isPaperCheckInProgress && activeCardId !== grammarFeedbacks[0].id))
    ) {
      const feedbackId = grammarFeedbacks[0].id
      setActiveCard(feedbackId)
      return
    }

    const feedback = grammarFeedbacks.find((feedback) => feedback.id === activeCardId)

    if (!feedback) return
    const { offset, length } = getGrammarPosition(feedback, sentenceOffsets)
    const { from, to } = editor.state.selection
    editor.commands.setCustomHighlight({
      id: feedback.id,
      type: feedback.feedbackType,
      offset,
      length,
    })
    editor.commands.setTextSelection({ from, to })
    // on leaving screan or switching tab
    return () => {
      removePreviousHighlight()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editor,
    editor.commands,
    grammarFeedbacks,
    sentenceOffsets,
    setActiveCard,
    activeCardId,
    removePreviousHighlight,
    grammarFeedbacksCount,
  ])

  return (
    <GrammarSectionWrapper>
      {grammarFeedbacks.length > 0 && (
        <GrammarCardsContainer isUndoRedoEnabled={isUndoRedoEnabled}>
          {grammarFeedbacks.map((grammarFeeback, index) => (
            <GrammarCard
              feedback={grammarFeeback}
              key={grammarFeeback.id}
              isExpanded={activeCardId === grammarFeeback.id}
              handleCardToggle={() => handleCardClick(grammarFeeback.id)}
              handleDeleteClick={() =>
                handleGrammarDeleteClick(
                  grammarFeeback.id,
                  index,
                  grammarFeedbacks,
                  sentenceOffsets,
                  dispatch,
                  setActiveCard,
                  initiateSurveyOnCardInteraction
                )
              }
              setFocus={() => {
                setNextCardActiveAfterDeleteOrUpdate(
                  index,
                  grammarFeedbacks,
                  setActiveCard
                )
              }}
            />
          ))}
        </GrammarCardsContainer>
      )}
    </GrammarSectionWrapper>
  )
}
export default GrammarSection
